import type { QuestionType } from '@eduborg/shared';
import type { OptionShape, QuestionShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Read-only render of a question prompt + the matching student-input control.
 * Drives the AssessmentTaker. The question's `type` discriminates the input
 * shape; the response shape mirrors `Response` in @eduborg/shared/grading.
 *
 * Question types not yet inputtable (hotspot, file_upload, code, drag_drop)
 * show an explanatory stub — they're still authorable in the editor.
 */

export type QuestionResponseValue =
  | { type: 'multiple_choice'; selectedOptionId: string }
  | { type: 'multiple_select'; selectedOptionIds: string[] }
  | { type: 'true_false'; value: boolean }
  | { type: 'short_answer'; text: string }
  | { type: 'long_answer'; html: string }
  | { type: 'numeric'; value: number; unit?: string }
  | { type: 'fill_in_blank'; values: string[] }
  | { type: 'matching'; pairs: Array<{ left: string; right: string }> }
  | { type: 'ordering'; order: string[] }
  | { type: 'classification'; assignments: Array<{ itemId: string; bucketId: string }> }
  | { type: 'formula'; expression: string }
  | null;

export function emptyResponseFor(
  type: QuestionType,
  options: OptionShape[],
): QuestionResponseValue {
  switch (type) {
    case 'multiple_choice':
      return { type: 'multiple_choice', selectedOptionId: '' };
    case 'multiple_select':
      return { type: 'multiple_select', selectedOptionIds: [] };
    case 'true_false':
      return { type: 'true_false', value: false };
    case 'short_answer':
      return { type: 'short_answer', text: '' };
    case 'long_answer':
      return { type: 'long_answer', html: '' };
    case 'numeric':
      return { type: 'numeric', value: 0 };
    case 'fill_in_blank':
      return { type: 'fill_in_blank', values: [] };
    case 'matching':
      return {
        type: 'matching',
        pairs: options.map((o) => ({ left: o.id, right: '' })),
      };
    case 'ordering':
      return { type: 'ordering', order: options.map((o) => o.id) };
    case 'classification':
      return { type: 'classification', assignments: [] };
    case 'formula':
      return { type: 'formula', expression: '' };
    default:
      return null;
  }
}

export function QuestionRenderer({
  question,
  value,
  onChange,
}: {
  question: QuestionShape;
  value: QuestionResponseValue;
  onChange: (next: QuestionResponseValue) => void;
}) {
  const v = value as Exclude<QuestionResponseValue, null>;

  switch (question.type) {
    case 'multiple_choice':
      return (
        <ul className="space-y-2">
          {question.options.map((opt) => (
            <li key={opt.id}>
              <label
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 transition-colors',
                  v?.type === 'multiple_choice' &&
                    v.selectedOptionId === opt.id &&
                    'border-accent bg-accent/5',
                )}
              >
                <input
                  type="radio"
                  name={`q-${question.id}`}
                  checked={v?.type === 'multiple_choice' && v.selectedOptionId === opt.id}
                  onChange={() => onChange({ type: 'multiple_choice', selectedOptionId: opt.id })}
                  className="h-4 w-4"
                />
                <span className="text-sm">{opt.label || <em>untitled option</em>}</span>
              </label>
            </li>
          ))}
        </ul>
      );

    case 'multiple_select':
      return (
        <ul className="space-y-2">
          {question.options.map((opt) => {
            const checked = v?.type === 'multiple_select' && v.selectedOptionIds.includes(opt.id);
            return (
              <li key={opt.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 transition-colors',
                    checked && 'border-accent bg-accent/5',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const current = (
                        v?.type === 'multiple_select' ? v.selectedOptionIds : []
                      ) as string[];
                      const next = e.target.checked
                        ? [...current, opt.id]
                        : current.filter((id) => id !== opt.id);
                      onChange({ type: 'multiple_select', selectedOptionIds: next });
                    }}
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">{opt.label || <em>untitled option</em>}</span>
                </label>
              </li>
            );
          })}
        </ul>
      );

    case 'true_false':
      return (
        <div className="grid grid-cols-2 gap-2">
          {[true, false].map((b) => {
            const checked = v?.type === 'true_false' && v.value === b;
            return (
              <button
                key={String(b)}
                type="button"
                onClick={() => onChange({ type: 'true_false', value: b })}
                className={cn(
                  'rounded-xl border px-4 py-3 text-sm font-semibold transition-colors',
                  checked
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-secondary',
                )}
              >
                {b ? 'True' : 'False'}
              </button>
            );
          })}
        </div>
      );

    case 'short_answer':
      return (
        <input
          type="text"
          value={v?.type === 'short_answer' ? v.text : ''}
          onChange={(e) => onChange({ type: 'short_answer', text: e.target.value })}
          className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          placeholder="Your answer"
        />
      );

    case 'long_answer':
      return (
        <textarea
          rows={6}
          value={v?.type === 'long_answer' ? v.html : ''}
          onChange={(e) => onChange({ type: 'long_answer', html: e.target.value })}
          placeholder="Write your response…"
          className="block w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
        />
      );

    case 'numeric':
      return (
        <input
          type="number"
          step="any"
          value={v?.type === 'numeric' ? v.value : 0}
          onChange={(e) => onChange({ type: 'numeric', value: Number(e.target.value) })}
          className="block w-40 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring"
        />
      );

    case 'fill_in_blank': {
      const blanks = parseBlanks(question.payloadJson);
      const values = (v?.type === 'fill_in_blank' ? v.values : []) as string[];
      return (
        <ol className="space-y-2">
          {blanks.map((_, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">#{i + 1}</span>
              <input
                value={values[i] ?? ''}
                onChange={(e) => {
                  const next = [...values];
                  while (next.length <= i) next.push('');
                  next[i] = e.target.value;
                  onChange({ type: 'fill_in_blank', values: next });
                }}
                className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring"
              />
            </li>
          ))}
        </ol>
      );
    }

    case 'matching':
      return (
        <ul className="space-y-2">
          {question.options.map((opt) => {
            const pair = (v?.type === 'matching' ? v.pairs : []).find((p) => p.left === opt.id);
            return (
              <li key={opt.id} className="flex items-center gap-2">
                <span className="flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm">
                  {opt.label || <em>untitled</em>}
                </span>
                <span className="text-muted-foreground">→</span>
                <input
                  value={pair?.right ?? ''}
                  onChange={(e) => {
                    const current = v?.type === 'matching' ? v.pairs : [];
                    const next = current.filter((p) => p.left !== opt.id);
                    next.push({ left: opt.id, right: e.target.value });
                    onChange({ type: 'matching', pairs: next });
                  }}
                  placeholder="Match"
                  className="w-48 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring"
                />
              </li>
            );
          })}
        </ul>
      );

    case 'ordering':
      return <OrderingInput question={question} value={v} onChange={onChange} />;

    case 'classification':
      // Phase-3 stub: classification is bucket-drag UX. We expose a minimal
      // pick-bucket-per-item dropdown so it's still answerable while the
      // drag UI lands later.
      return <ClassificationStub question={question} value={v} onChange={onChange} />;

    case 'formula':
      return (
        <input
          type="text"
          value={v?.type === 'formula' ? v.expression : ''}
          onChange={(e) => onChange({ type: 'formula', expression: e.target.value })}
          placeholder="Enter your expression"
          className="block w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm outline-none focus:border-ring"
        />
      );

    case 'hotspot':
    case 'file_upload':
    case 'code':
    case 'drag_drop':
      return <NotYetTakeable type={question.type} />;
  }
}

/* ─── Sub-renderers ────────────────────────────────────────────────────── */

function OrderingInput({
  question,
  value,
  onChange,
}: {
  question: QuestionShape;
  value: Extract<QuestionResponseValue, { type: 'ordering' }> | null | { type: string };
  onChange: (next: QuestionResponseValue) => void;
}) {
  const order =
    value && (value as { type: string }).type === 'ordering'
      ? (value as Extract<QuestionResponseValue, { type: 'ordering' }>).order
      : question.options.map((o) => o.id);

  const move = (idx: number, delta: number) => {
    const next = [...order];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onChange({ type: 'ordering', order: next });
  };

  return (
    <ol className="space-y-1.5">
      {order.map((id, idx) => {
        const opt = question.options.find((o) => o.id === id);
        return (
          <li
            key={id}
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2"
          >
            <span className="font-mono text-xs text-muted-foreground">{idx + 1}.</span>
            <span className="flex-1 text-sm">{opt?.label ?? id}</span>
            <button
              type="button"
              onClick={() => move(idx, -1)}
              disabled={idx === 0}
              className="rounded p-1 hover:bg-secondary disabled:opacity-40"
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(idx, 1)}
              disabled={idx === order.length - 1}
              className="rounded p-1 hover:bg-secondary disabled:opacity-40"
              aria-label="Move down"
            >
              ↓
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function ClassificationStub({
  question,
  value,
  onChange,
}: {
  question: QuestionShape;
  value: QuestionResponseValue;
  onChange: (next: QuestionResponseValue) => void;
}) {
  // Buckets = unique matchValues across options. The instructor sets
  // matchValue to identify the target bucket per item.
  const buckets = Array.from(
    new Set(question.options.map((o) => o.matchValue ?? '').filter(Boolean)),
  );
  const assignments = (
    value && (value as { type: string }).type === 'classification'
      ? (value as Extract<QuestionResponseValue, { type: 'classification' }>).assignments
      : []
  ) as Array<{ itemId: string; bucketId: string }>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Pick a bucket for each item. (Drag-and-drop UI lands in Phase 4 polish.)
      </p>
      <ul className="space-y-1.5">
        {question.options.map((opt) => {
          const current = assignments.find((a) => a.itemId === opt.id)?.bucketId ?? '';
          return (
            <li key={opt.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{opt.label}</span>
              <select
                value={current}
                onChange={(e) => {
                  const next = assignments.filter((a) => a.itemId !== opt.id);
                  if (e.target.value) next.push({ itemId: opt.id, bucketId: e.target.value });
                  onChange({ type: 'classification', assignments: next });
                }}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
              >
                <option value="">— pick —</option>
                {buckets.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NotYetTakeable({ type }: { type: QuestionType }) {
  const labels: Partial<Record<QuestionType, string>> = {
    hotspot: 'Hotspot questions need an image canvas — coming with R2 integration.',
    file_upload: 'File-upload questions need R2 — Phase 0 cont’d.',
    code: "Code questions run in the Phase-5 sandbox; for now they're instructor-graded.",
    drag_drop: 'Drag-and-drop UI ships in Phase 4 polish.',
  };
  return (
    <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-4 text-xs text-muted-foreground">
      {labels[type] ?? 'Not yet takeable.'}
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function parseBlanks(payloadJson: string | null): unknown[] {
  if (!payloadJson) return [{}];
  try {
    const parsed = JSON.parse(payloadJson) as { accepted?: unknown[] };
    if (Array.isArray(parsed.accepted)) return parsed.accepted;
  } catch {
    /* */
  }
  return [{}];
}
